/**
 * StandardReviewInbox Component
 * Adapted from Tim-Combo ApprovalInbox for EA-Agile standards review
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FiClipboard, FiClock, FiEye, FiRefreshCw } from 'react-icons/fi';
import { HiCheckCircle, HiExclamationCircle, HiXCircle } from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';
import type { StandardReviewItem } from '../pages/StandardsReviewPage';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface StandardReviewInboxProps {
  onReviewSelect?: (review: StandardReviewItem) => void;
  selectedReviewId?: string;
  onReviewsLoad?: (reviews: StandardReviewItem[]) => void;
}

const StandardReviewInbox: React.FC<StandardReviewInboxProps> = ({
  onReviewSelect,
  selectedReviewId,
  onReviewsLoad
}) => {
  const { user, accessToken } = useAuth();
  const [reviews, setReviews] = useState<StandardReviewItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);

  // Fetch pending reviews for current user
  const fetchPendingReviews = useCallback(async (): Promise<void> => {
    if (!user) {
      console.warn('Cannot fetch reviews: user not authenticated');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await Make_Authorized_API_Call<StandardReviewItem>(
        API_ENDPOINTS.STANDARD_REVIEW.PENDING,
        'GET',
        undefined,
        { params: { reviewer_email: user.email } }
      );

      if (isSuccessResponse(response)) {
        const pendingReviews = response.data?.Records || [];

        // Calculate priority and sort
        const reviewsWithPriority = pendingReviews.map((review: any) => {
          const createdDate = new Date(review.created_at);
          const daysSinceCreated = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

          let priority: 'high' | 'normal' | 'low' = 'normal';
          if (review.standard_type === 'security' || daysSinceCreated > 7) {
            priority = 'high';
          } else if (daysSinceCreated > 3) {
            priority = 'normal';
          } else {
            priority = 'low';
          }

          return {
            ...review,
            priority
          };
        });

        // Sort by priority then by date
        reviewsWithPriority.sort((a: any, b: any) => {
          const priorityOrder: {[key: string]: number} = { high: 3, normal: 2, low: 1 };
          const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
          if (priorityDiff !== 0) return priorityDiff;

          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });

        setReviews(reviewsWithPriority);
        if (onReviewsLoad) {
          onReviewsLoad(reviewsWithPriority);
        }

        console.log(`Loaded ${reviewsWithPriority.length} pending reviews`);
      } else {
        throw new Error(response.message || 'Failed to fetch pending reviews');
      }
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load reviews');
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  }, [user, onReviewsLoad]);

  // Initial load
  useEffect(() => {
    fetchPendingReviews();
  }, [fetchPendingReviews]);

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchPendingReviews();
  };

  // Handle review selection
  const handleReviewClick = (review: StandardReviewItem) => {
    if (onReviewSelect) {
      onReviewSelect(review);
    }
  };

  // Get priority badge styling
  const getPriorityBadge = (priority: 'high' | 'normal' | 'low') => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'normal':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <HiCheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <HiXCircle className="h-5 w-5 text-red-600" />;
      case 'changes_requested':
        return <HiExclamationCircle className="h-5 w-5 text-yellow-600" />;
      default:
        return <FiClock className="h-5 w-5 text-gray-400" />;
    }
  };

  if (isLoading && reviews.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pending reviews...</p>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <HiXCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Reviews</h3>
            <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
            <button
              onClick={handleRefresh}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <FiClipboard className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No Pending Reviews</h3>
        <p className="mt-2 text-sm text-gray-500">
          You don't have any draft standards awaiting your review at the moment.
        </p>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <FiRefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with refresh button */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Pending Reviews ({reviews.length})
          </h2>
          <p className="text-sm text-gray-500">Click on a review to take action</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <FiRefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Review items list */}
      <div className="space-y-3">
        {reviews.map((review) => (
          <div
            key={review.review_id}
            onClick={() => handleReviewClick(review)}
            className={`
              bg-white rounded-lg border-2 p-4 cursor-pointer transition-all
              ${selectedReviewId === review.review_id
                ? 'border-blue-500 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
              }
            `}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {/* Title and type */}
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="text-base font-semibold text-gray-900 truncate">
                    {review.standard_title}
                  </h3>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {review.standard_type}
                  </span>
                </div>

                {/* Focus category and priority */}
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-sm text-gray-600">
                    {review.focus_category}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getPriorityBadge((review as any).priority)}`}>
                    {(review as any).priority?.toUpperCase()}
                  </span>
                </div>

                {/* Linked incident */}
                {review.incident_title && (
                  <div className="text-sm text-gray-500 mb-2">
                    <span className="font-medium">From incident:</span> {review.incident_title}
                  </div>
                )}

                {/* AI confidence */}
                {review.ai_confidence_score && (
                  <div className="text-sm text-gray-500">
                    <span className="font-medium">AI Confidence:</span> {(review.ai_confidence_score * 100).toFixed(0)}%
                  </div>
                )}

                {/* Metadata */}
                <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                  <div className="flex items-center">
                    <FiClock className="mr-1 h-3 w-3" />
                    {new Date(review.created_at).toLocaleDateString()}
                  </div>
                  {review.approval_group_id && (
                    <div className="flex items-center">
                      <span>Group: {review.approval_group_id}</span>
                    </div>
                  )}
                  <div className="flex items-center">
                    <span>Round {review.review_round}</span>
                  </div>
                </div>
              </div>

              {/* Status icon */}
              <div className="ml-4 flex-shrink-0">
                {getStatusIcon(review.status)}
              </div>
            </div>

            {/* View button */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center">
                <FiEye className="mr-1 h-4 w-4" />
                Review Standard
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StandardReviewInbox;
